// Smart image preloading system with cache management
import React from 'react';
import { buildVariantUrl } from './photoCache';

class ImagePreloader {
  constructor() {
    this.preloadedImages = new Map(); // URL -> Image object
    this.loadingImages = new Map(); // URL -> Promise
    this.maxCacheSize = 50; // Maximum number of images to keep in memory
    this.preloadRadius = 3; // Number of images to preload in each direction
    
    // Track user mutations for instant cache invalidation
    this.mutationListeners = new Set();
    
    // Performance tracking
    this.stats = {
      hits: 0,
      misses: 0,
      preloads: 0
    };
  }
  
  // Generate URL for an image
  getImageUrl(image, variant = 'lg') {
    if (!image) return null;
    return buildVariantUrl(image, variant);
  }
  
  // Preload a single image
  async preloadImage(url) {
    if (!url) return null;
    
    // Already loaded
    if (this.preloadedImages.has(url)) {
      this.stats.hits++;
      return this.preloadedImages.get(url);
    }
    
    // Currently loading
    if (this.loadingImages.has(url)) {
      return this.loadingImages.get(url);
    }
    
    this.stats.misses++;
    this.stats.preloads++;
    
    // Start loading
    const loadPromise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedImages.set(url, img);
        this.loadingImages.delete(url);
        this.enforceMaxCache();
        resolve(img);
      };
      
      img.onerror = () => {
        this.loadingImages.delete(url);
        reject(new Error(`Failed to load image: ${url}`));
      };
      
      // High priority for user-visible images
      img.fetchPriority = 'high';
      img.decoding = 'async';
      img.src = url;
    });
    
    this.loadingImages.set(url, loadPromise);
    return loadPromise;
  }
  
  // Preload images around current index
  async preloadAround(images, currentIndex, variant = 'lg') {
    if (!images || images.length === 0) return;
    
    const promises = [];
    
    // Current image - highest priority
    const currentUrl = this.getImageUrl(images[currentIndex], variant);
    if (currentUrl) {
      promises.push(this.preloadImage(currentUrl));
    }
    
    // Preload next images (more important than previous)
    for (let i = 1; i <= this.preloadRadius; i++) {
      const nextIndex = (currentIndex + i) % images.length;
      const nextUrl = this.getImageUrl(images[nextIndex], variant);
      if (nextUrl) {
        promises.push(this.preloadImage(nextUrl));
      }
    }
    
    // Preload previous images
    for (let i = 1; i <= Math.floor(this.preloadRadius / 2); i++) {
      const prevIndex = (currentIndex - i + images.length) % images.length;
      const prevUrl = this.getImageUrl(images[prevIndex], variant);
      if (prevUrl) {
        promises.push(this.preloadImage(prevUrl));
      }
    }
    
    // Load in parallel but don't wait for all
    Promise.allSettled(promises).catch(() => {
      // Silent fail for preloads
    });
  }
  
  // Check if image is ready (already loaded)
  isReady(image, variant = 'lg') {
    const url = this.getImageUrl(image, variant);
    return url && this.preloadedImages.has(url);
  }
  
  // Get loading promise if image is currently loading
  getLoadingPromise(image, variant = 'lg') {
    const url = this.getImageUrl(image, variant);
    return url ? this.loadingImages.get(url) : null;
  }
  
  // Enforce maximum cache size (LRU)
  enforceMaxCache() {
    if (this.preloadedImages.size <= this.maxCacheSize) return;
    
    // Remove oldest entries
    const toRemove = this.preloadedImages.size - this.maxCacheSize;
    const entries = Array.from(this.preloadedImages.entries());
    
    for (let i = 0; i < toRemove; i++) {
      const [url] = entries[i];
      this.preloadedImages.delete(url);
    }
  }
  
  // Invalidate cache for specific images
  invalidateImages(imageIds) {
    if (!imageIds || imageIds.length === 0) return;
    
    const idsSet = new Set(imageIds);
    
    // Remove from preloaded cache
    for (const [url] of this.preloadedImages.entries()) {
      // Check if URL contains any of the image IDs
      for (const id of idsSet) {
        if (url.includes(id)) {
          this.preloadedImages.delete(url);
          break;
        }
      }
    }
    
    // Cancel loading if in progress
    for (const [url] of this.loadingImages.entries()) {
      for (const id of idsSet) {
        if (url.includes(id)) {
          this.loadingImages.delete(url);
          break;
        }
      }
    }
  }
  
  // Clear entire cache (for major mutations)
  clearCache() {
    this.preloadedImages.clear();
    this.loadingImages.clear();
  }
  
  // Invalidate based on mutation type
  handleMutation(type, data = {}) {
    switch (type) {
      case 'upload':
      case 'delete':
      case 'albumChange':
        // Major changes - clear everything
        this.clearCache();
        break;
        
      case 'rotate':
      case 'edit':
      case 'like':
      case 'unlike':
        // Specific image changed
        if (data.imageId) {
          this.invalidateImages([data.imageId]);
        }
        break;
        
      case 'batchEdit':
      case 'batchDelete':
        // Multiple images changed
        if (data.imageIds) {
          this.invalidateImages(data.imageIds);
        }
        break;
    }
    
    // Notify listeners
    this.notifyMutation(type, data);
  }
  
  // Register mutation listener
  onMutation(callback) {
    this.mutationListeners.add(callback);
    return () => this.mutationListeners.delete(callback);
  }
  
  // Notify all mutation listeners
  notifyMutation(type, data) {
    for (const listener of this.mutationListeners) {
      try {
        listener(type, data);
      } catch (e) {
        console.error('Mutation listener error:', e);
      }
    }
  }
  
  // Get preload statistics
  getStats() {
    return {
      ...this.stats,
      cacheSize: this.preloadedImages.size,
      loading: this.loadingImages.size,
      hitRate: this.stats.hits / (this.stats.hits + this.stats.misses) || 0
    };
  }
  
  // React hook helper
  static usePreloader() {
    const [preloader] = React.useState(() => new ImagePreloader());
    
    React.useEffect(() => {
      return () => {
        // Cleanup on unmount
        preloader.clearCache();
      };
    }, [preloader]);
    
    return preloader;
  }
}

// Export singleton for app-wide use
export const imagePreloader = new ImagePreloader();

// React hook for preloading with automatic cleanup
export function useImagePreloader(images, currentIndex, options = {}) {
  const { variant = 'lg', enabled = true } = options;
  const [loading, setLoading] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  
  React.useEffect(() => {
    if (!enabled || !images || images.length === 0) return;
    
    // Check if current image is ready
    const currentImage = images[currentIndex];
    if (imagePreloader.isReady(currentImage, variant)) {
      setReady(true);
      setLoading(false);
    } else {
      setReady(false);
      setLoading(true);
      
      // Load current image
      const url = imagePreloader.getImageUrl(currentImage, variant);
      if (url) {
        imagePreloader.preloadImage(url).then(() => {
          setReady(true);
          setLoading(false);
        }).catch(() => {
          setLoading(false);
        });
      }
    }
    
    // Preload surrounding images
    imagePreloader.preloadAround(images, currentIndex, variant);
    
  }, [images, currentIndex, variant, enabled]);
  
  return { loading, ready, preloader: imagePreloader };
}

// Export mutation handler for use in components
export function invalidateImageCache(type, data) {
  imagePreloader.handleMutation(type, data);
}
